import React from 'react';
import T from 'prop-types';
// components
import SurveyBox from './components/SurveyBox';
import Panel from 'components/Panel';
import { FormattedMessage } from 'react-intl';
import ToggleSwitcher from 'components/ToggleSwitcher';
import Input from 'components/Input';
import Button from 'components/Button';
import TenantSettingTogglePanel from 'components/TenantSettingTogglePanel/TenantSettingTogglePanel';
import { optionsSetpointSource, getOptLabel } from './utils';

const normalizeValue = (value) => {
  if (!value) {
    return 0;
  }
  if (Number(value) > 99) {
    return 99;
  }
  return value.length > 1 ? value.replace(/^0+/, '') : value;
};

const DailyCheckup = ({
  tenant_settings: {
    surveys,
    custom_survey,
    track_temperature,
    track_water_usage,
    water_consumption_monitoring,
    dc_migrations_limited_access,
    dc_mortality_media_prompt,
    require_water_usage = false,
    require_temperature = false,
    water_usage_farm_users,
    water_usage_vets_alerts,
    water_usage_notes_notifications_off,
    temperature_notes_notifications_off,
    edit_mode_mentions,
  },
  temperature_setpoint_source = 'weight_based_source',
  updateSettings,
  setSettings,
  waterPercents,
  updateWaterPercent,
}) => {
  const renderToggleTitle = (labelKey, valueLabel = '') => (
    <div className="toggleTitle">
      <FormattedMessage
        tagName="b"
        id={`general.settings.${labelKey}`}
        values={{ label: <b className="primary">{valueLabel}</b> }}
      />
      <p className="toggleSub">
        <FormattedMessage
          id={`general.settings.${labelKey}Info`}
          values={{ label: valueLabel }}
        />
      </p>
    </div>
  );
  return (
    <Panel>
      <Panel.Heading title={<FormattedMessage id="general.dailyCheckup" />} />
      <Panel.Body>
        <section className="setting-section">
          <ToggleSwitcher
            name="track_water_usage"
            className="small-12 medium-12"
            text={<FormattedMessage id="component.settings.waterUsageTracking" />}
            description={
              <FormattedMessage id="component.settings.waterUsageTrackingDesc" />
          }
            checked={track_water_usage}
            onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })
          }
          >
            {track_water_usage && (
          <>
            <ToggleSwitcher
              name="water_consumption_monitoring"
              className="small-12 medium-12"
              text={<FormattedMessage id="component.settings.waterConsumptionMonitoring" />}
              description={<FormattedMessage id="component.settings.waterConsumptionMonitoringDesc" />}
              checked={water_consumption_monitoring}
              onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })
              }
            >
              {water_consumption_monitoring && (
              <div className="generate-alert">
                <div className="content-container">
                  <span className="description">
                    <FormattedMessage id="component.settings.waterConsumptionMonitoring.alertBoxDesc" />
                  </span>
                  <div className="percent-box">
                    <Input
                      type="number"
                      value={normalizeValue(String(waterPercents))}
                      onChange={({ target: { value } }) => updateWaterPercent(Number(value))}
                    />
                    <span>%</span>
                  </div>
                </div>
                <Button primary onClick={() => updateSettings({ water_consumption_drop_limit: waterPercents })}>
                  <FormattedMessage id="general.button.save" />
                </Button>
              </div>
              )}
            </ToggleSwitcher>
            <ToggleSwitcher
              name="water_usage_vets_alerts"
              className="small-12 medium-12"
              text={<FormattedMessage id="component.settings.waterUsageVetsAlerts" />}
              description={<FormattedMessage id="component.settings.waterUsageVetsAlertsDesc" />}
              checked={water_usage_vets_alerts}
              onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })}
            />
            <ToggleSwitcher
              name="water_usage_farm_users"
              className="small-12 medium-12"
              text={<FormattedMessage id="component.settings.waterUsageFarmUsers" />}
              description={<FormattedMessage id="component.settings.waterUsageFarmUsersDesc" />}
              checked={water_usage_farm_users}
              onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })}
            />
            <ToggleSwitcher
              name="water_usage_notes_notifications_off"
              className="small-12 medium-12"
              text={<FormattedMessage id="component.settings.waterUsageNotesNotificationsOff" />}
              description={<FormattedMessage id="component.settings.waterUsageNotesNotificationsOffDesc" />}
              checked={water_usage_notes_notifications_off}
              onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })}
            />
            <ToggleSwitcher
              name="require_water_usage"
              className="small-12 medium-12"
              text={<FormattedMessage id="container.myProfile.requireWaterUsage" />}
              description={<FormattedMessage id="container.myProfile.requireWaterUsageDesc" />}
              checked={require_water_usage}
              onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })}
            />
          </>
            )}
          </ToggleSwitcher>
        </section>

        <section className="setting-section">
          <ToggleSwitcher
            name="track_temperature"
            className="small-12 medium-8"
            text={
              <FormattedMessage id="component.settings.temperatureTracking" />
          }
            description={
              <FormattedMessage id="component.settings.temperatureTrackingDesc" />
          }
            checked={track_temperature}
            onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })
          }
          >
            {track_temperature && (
            <>
              <ToggleSwitcher
                name="temperature_notes_notifications_off"
                className="small-12 medium-12 mt-15"
                text={<FormattedMessage id="component.settings.temperatureNotesNotificationsOff" />}
                description={<FormattedMessage id="component.settings.temperatureNotesNotificationsOffDesc" />}
                checked={temperature_notes_notifications_off}
                onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })}
              />
              <ToggleSwitcher
                name="require_temperature"
                className="small-12 medium-12"
                text={<FormattedMessage id="container.myProfile.requireTemperature" />}
                description={<FormattedMessage id="container.myProfile.requireTemperatureDesc" />}
                checked={require_temperature}
                onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })}
              />
            </>
            )}
          </ToggleSwitcher>
        </section>

        <section className="setting-section">
          <TenantSettingTogglePanel
            className="small-12 medium-8 pl-0"
            options={optionsSetpointSource}
            value={temperature_setpoint_source}
            onChange={updateSettings}
            settingKey="temperature_setpoint_source"
          >
            {renderToggleTitle(
              'temperatureSetpointSource',
              getOptLabel(optionsSetpointSource, temperature_setpoint_source)
            )}
          </TenantSettingTogglePanel>
        </section>

        <section className="setting-section">
          <ToggleSwitcher
            name="dc_migrations_limited_access"
            className="small-12 medium-8"
            text={<FormattedMessage id="component.settings.dcMovementsEditingConstraint" />}
            description={
              <FormattedMessage id="component.settings.dcMovementsEditingConstraintDesc" />
          }
            checked={dc_migrations_limited_access}
            onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })}
          />
        </section>

        <section className="setting-section">
          <ToggleSwitcher
            name="custom_survey"
            className="small-12 medium-8"
            text={<FormattedMessage id="component.settings.customSurvey.title" />}
            description={
              <FormattedMessage id="component.settings.customSurvey.desc" />
          }
            checked={custom_survey}
            onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })
          }
          >
            {custom_survey && (
            <SurveyBox surveys={surveys} setSettings={setSettings} />
            )}
          </ToggleSwitcher>
        </section>
        <section className="setting-section">
          <ToggleSwitcher
            name="dc_mortality_media_prompt"
            className="small-12 medium-8"
            text={<FormattedMessage id="component.settings.mortalityMedia" />}
            description={<FormattedMessage id="component.settings.mortalityMediaDesc" />}
            checked={dc_mortality_media_prompt}
            onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })}
          />
        </section>
        <section className="setting-section">
          <ToggleSwitcher
            name="edit_mode_mentions"
            className="small-12 medium-8"
            text={<FormattedMessage id="component.settings.editModeMentions" />}
            description={<FormattedMessage id="component.settings.editModeMentionsDesc" />}
            checked={edit_mode_mentions}
            onChange={({ target: { name, checked } }) => updateSettings({ [name]: checked })}
          />
        </section>
      </Panel.Body>
    </Panel>
  );
};

DailyCheckup.propTypes = {
  updateSettings: T.func.isRequired,
  tenant_settings: T.object.isRequired,
  setSettings: T.func.isRequired,
  waterPercents: T.number.isRequired,
  updateWaterPercent: T.func.isRequired,
  temperature_setpoint_source: T.string,
};

export default DailyCheckup;
